export type PlatformLegalLocale = "hu" | "en";
export type PlatformPolicyDocument = {
  title: string;
  body: string;
  checksum: string;
};
export type PlatformPolicyRelease = {
  id: number;
  release_key: string;
  checksum: string;
  effective_date: string;
  locale: PlatformLegalLocale;
  notice: string;
  documents: Record<
    "terms" | "privacy" | "provider_agreement",
    PlatformPolicyDocument
  >;
};
export type PendingPlatformPolicy = PlatformPolicyRelease & {
  guardian_required: boolean;
};
export type ProviderLegalRegistration = {
  provider_type: string;
  release_id: number;
  agreement_checksum: string;
  locale: PlatformLegalLocale;
  legal_name: string;
  legal_address: string;
  tax_number: string;
  representative_confirmed: boolean;
};

const checksum = (value: unknown) =>
  typeof value === "string" && /^[a-f0-9]{64}$/.test(value);
export function isPlatformRelease(
  value: unknown,
): value is PlatformPolicyRelease {
  const item = value as PlatformPolicyRelease | undefined;
  return Boolean(
    item &&
      Number.isInteger(item.id) &&
      item.id > 0 &&
      typeof item.release_key === "string" &&
      item.release_key &&
      checksum(item.checksum) &&
      ["hu", "en"].includes(item.locale) &&
      typeof item.notice === "string" &&
      item.notice &&
      typeof item.effective_date === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(item.effective_date) &&
      ["terms", "privacy", "provider_agreement"].every((kind) => {
        const doc =
          item.documents?.[kind as keyof PlatformPolicyRelease["documents"]];
        return (
          doc &&
          typeof doc.body === "string" &&
          doc.body.trim() &&
          typeof doc.title === "string" &&
          doc.title.trim() &&
          checksum(doc.checksum)
        );
      }),
  );
}
export function isPlatformPending(
  value: unknown,
): value is { items: PendingPlatformPolicy[] } {
  const items = (value as { items?: unknown[] })?.items;
  return (
    Array.isArray(items) &&
    items.length <= 1 &&
    items.every(
      (item) =>
        isPlatformRelease(item) &&
        typeof (item as PendingPlatformPolicy).guardian_required === "boolean",
    )
  );
}

export type PlatformPolicyAcceptanceInput = {
  release_id: number;
  checksum: string;
  locale: PlatformLegalLocale;
  terms_accepted: boolean;
  privacy_acknowledged: boolean;
  capacity: "self" | "guardian";
};
export type LegalRegistrationState = {
  valid: boolean;
  platform_acceptance?: PlatformPolicyAcceptanceInput;
  provider_legal_registrations: ProviderLegalRegistration[];
};
